import { useState, type SyntheticEvent } from "react";
import { useMutation, useQuery } from "convex/react";
import { Bot, Hash, Lock } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/button";
import { Dialog } from "../ui/Dialog";
import { errorMessage } from "./format";
import type { MeReady } from "./types";

export function CreateChannelDialog({ me, onClose, onCreated }: { me: MeReady; onClose: () => void; onCreated: (id: string) => void }) {
  const createChannel = useMutation(api.communication.createChannel);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [isPrivate, setPrivate] = useState(false);
  const [classification, setClassification] = useState<"standard" | "confidential" | "phi">(me.workspace.complianceMode === "hipaa" ? "confidential" : "standard");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const id = await createChannel({ name, isPrivate, classification, ...(topic.trim().length > 0 ? { topic: topic.trim() } : {}) });
      onCreated(id);
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Create a channel" onClose={onClose}>
      <form className="stack" onSubmit={(event) => { void submit(event); }}>
        <label className="field">Name<div className="field-row"><span className="field-prefix">{isPrivate ? <Lock size={14} /> : <Hash size={14} />}</span><input name="channel-name" autoComplete="off" value={name} onChange={(event) => { setName(event.target.value); }} placeholder="front-desk" maxLength={80} required /></div><small>Lowercase, dashes instead of spaces.</small></label>
        <label className="field">Topic (optional)<input name="channel-topic" autoComplete="off" value={topic} onChange={(event) => { setTopic(event.target.value); }} placeholder="What is this channel for?" maxLength={250} /></label>
        <label className="check"><input name="private-channel" type="checkbox" checked={isPrivate} onChange={(event) => { setPrivate(event.target.checked); }} /> Private — only invited people and agents can see it</label>
        <label className="field">Data level
          <select name="channel-classification" value={classification} onChange={(event) => { setClassification(event.target.value === "phi" ? "phi" : event.target.value === "confidential" ? "confidential" : "standard"); }}>
            <option value="standard">Standard — everyday company work</option>
            <option value="confidential">Confidential — sensitive business information</option>
            <option value="phi" disabled={me.workspace.complianceMode !== "hipaa" || me.billing?.trial === true}>{me.billing?.trial === true ? "Protected health information (not available during the free trial)" : "Protected health information (requires completed security gate)"}</option>
          </select>
          <small>This label controls which people and AI employees can use the channel.</small>
        </label>
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="dialog__actions"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" disabled={busy || name.trim().length === 0}>Create channel</Button></div>
      </form>
    </Dialog>
  );
}

export function BrowseChannelsDialog({ onClose, onJoined }: { onClose: () => void; onJoined: (id: string) => void }) {
  const channels = useQuery(api.communication.browseChannels, {});
  const join = useMutation(api.communication.joinChannel);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog title="Browse channels" onClose={onClose}>
      {channels === undefined ? <p className="loading-hint">Loading…</p> : channels.length === 0 ? <p className="nav-hint">You have joined every public channel.</p> : (
        <ul className="pick-list">
          {channels.map((channel) => (
            <li key={channel.id}>
              <Hash size={14} />
              <div><strong>{channel.name}</strong><span>{channel.memberCount} {channel.memberCount === 1 ? "member" : "members"}{channel.topic !== undefined ? ` · ${channel.topic}` : ""}</span></div>
              <Button size="compact" onClick={() => { void join({ conversationId: channel.id }).then(() => { onJoined(channel.id); }).catch((caught: unknown) => { setError(errorMessage(caught)); }); }}>Join</Button>
            </li>
          ))}
        </ul>
      )}
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
    </Dialog>
  );
}

export function NewDirectMessageDialog({ me, onClose, onCreated }: { me: MeReady; onClose: () => void; onCreated: (id: string) => void }) {
  const members = useQuery(api.communication.listMembers, {});
  const createDirect = useMutation(api.communication.createDirect);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const people = (members ?? []).filter((member) => member.active && member.principalId !== me.principalId && member.displayName.toLowerCase().includes(filter.toLowerCase()));
  return (
    <Dialog title="New direct message" onClose={onClose}>
      <input className="dialog-search" name="teammate-search" autoComplete="off" value={filter} onChange={(event) => { setFilter(event.target.value); }} placeholder="Find a teammate or agent…" aria-label="Find a teammate" />
      <ul className="pick-list">
        {members === undefined ? <li className="nav-hint">Loading…</li> : people.length === 0 ? <li className="nav-hint">Nobody matches.</li> : people.map((member) => (
          <li key={member.principalId}>
            <Avatar name={member.displayName} kind={member.kind} small online={member.agentOnline} photoUrl={member.avatarUrl} />
            <div><strong>{member.displayName}</strong><span>{member.kind === "openclaw" ? <><Bot size={11} /> AI teammate{member.agentOnline === true ? " · online" : " · offline"}</> : member.email ?? member.role}</span></div>
            <Button size="compact" onClick={() => { void createDirect({ otherPrincipalId: member.principalId }).then((id) => { onCreated(id); }).catch((caught: unknown) => { setError(errorMessage(caught)); }); }}>Message</Button>
          </li>
        ))}
      </ul>
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
    </Dialog>
  );
}
